import { useCallback, useState } from "react";
import { saveToStorage } from "@rentablo/api";
import { useExchangeOidcToken } from "@rentablo/api/apiv1";
import { useFirebaseOidcClient } from "./useFirebaseOidcClient";

type Props = {
  onTokenReceived: (accessToken: string) => Promise<void> | void;
};

type State = {
  loading: boolean;
  error: string | null;
};

export const useOidcLogin = ({ onTokenReceived }: Props) => {
  const [state, setState] = useState<State>({ loading: false, error: null });
  const { mutateAsync: exchangeOidcTokenAsync } = useExchangeOidcToken();
  const { signIn, error: firebaseError } = useFirebaseOidcClient();

  const login = useCallback(async () => {
    setState({ loading: true, error: null });

    try {
      if (firebaseError) {
        setState({
          loading: false,
          error: firebaseError,
        });
        return;
      }

      const idToken = await signIn();
      const loginToken = await exchangeOidcTokenAsync({
        oidcExchangeParams: { token: idToken },
      });

      if (!loginToken.access_token) {
        throw new Error("OIDC exchange response missing access_token");
      }

      saveToStorage(loginToken, true);

      await onTokenReceived(loginToken.access_token);

      setState({ loading: false, error: null });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("OIDC login failed", error);
      setState({
        loading: false,
        error:
          error instanceof Error &&
          error.message === "Firebase OIDC client is not initialized"
            ? "loginSSOPage.firebaseConfigError"
            : "loginSSOPage.genericError",
      });
    }
  }, [exchangeOidcTokenAsync, firebaseError, onTokenReceived, signIn]);

  return {
    login,
    loading: state.loading,
    errorKey: state.error,
  };
};
